import React, {useState} from 'react';
import {Grid, GridColumn, GridToolbar,} from '@progress/kendo-react-grid';
import styles from './_api-message-history-dialog.module.scss';
import {
  ApiMessageHistoryContentType,
  ApplicationActivityRecord
} from "../../../Interfaces/Application/ApiMessageHistory";
import {getEnumName, getEmployeeName} from "../../../utils/utils";
import {DialogProps} from "../DialogProps";
import {Button, ButtonGroup, Chip, DialogActions, DialogContent, DialogTitle} from "@mui/material";
import { hasValue } from '@amsanalytics/react-shared-library';
import { IEmployee } from "../../../Interfaces/Admin/IEmployee";

// Define types for our data structure
interface ActivityRecordRow extends ApplicationActivityRecord {
  expanded?: boolean;
}

interface ApiMessageHistoryDialogProps extends DialogProps<null> {
  activityRecords: ApplicationActivityRecord[];
  employees: IEmployee[];
  onClose(): void;
}

// Activity type color mapping
const getActivityTypeColor = (activityType: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
  switch (activityType) {
    case 'Status Change': return 'info';
    case 'Post': return 'success';
    case 'Desk': return 'warning';
    default: return 'default'; // API message types
  }
};

// Main component
export const ApiMessageHistoryDialog: React.FC<ApiMessageHistoryDialogProps> = (props) => {
  const [data] = useState<ActivityRecordRow[]>(props.activityRecords.map(r => ({...r, expanded: false})));
  const [expandedItemIds, setExpandedItemIds] = useState<string[]>([]);

  // Handle expanding/collapsing rows
  const onExpandChange = (event: { dataItem: ActivityRecordRow }) => {
    const { dataItem } = event;
    const currentExpandedState = expandedItemIds.includes(dataItem.id);

    setExpandedItemIds(
      currentExpandedState
        ? expandedItemIds.filter(id => id !== dataItem.id)
        : [...expandedItemIds, dataItem.id]
    );
  };

  // Expand all rows
  const expandAll = () => {
    setExpandedItemIds(data.filter(item => hasValue(item.detail)).map(item => item.id));
  };

  // Collapse all rows
  const collapseAll = () => {
    setExpandedItemIds([]);
  };

  // Format date for display
  const formatDate = (date: string): string => {
    return new Date(date).toLocaleString();
  };

  // Extract message preview
  const getMessagePreview = (message: string | undefined, maxLength = 80): string => {
    if (!hasValue(message)) return '';
    try {
      const jsonObj = JSON.parse(message!);
      const keys = Object.keys(jsonObj).slice(0, 3);
      return keys.join(', ') + (Object.keys(jsonObj).length > 3 ? '...' : '');
    } catch {
      if (message!.trim().startsWith('<') && message!.includes('</')) {
        return 'XML Document';
      }
      return message!.length > maxLength
        ? message!.substring(0, maxLength) + '...'
        : message!;
    }
  };

  // Status cell with color coding
  const StatusCell = (cellProps: { dataItem: ActivityRecordRow }) => {
    const { dataItem } = cellProps;
    const status = dataItem.httpStatusCode;
    if (!status) return <td></td>;

    let statusClass = styles.statusIndicator;
    if (status >= 200 && status < 300) {
      statusClass = styles.statusSuccess;
    } else if (status >= 300 && status < 400) {
      statusClass = styles.statusRedirect;
    } else if (status >= 400 && status < 500) {
      statusClass = styles.statusError;
    } else if (status >= 500) {
      statusClass = styles.statusServerError;
    }

    return (
      <td>
        <span className={statusClass}>{status}</span>
      </td>
    );
  };

  // Activity type cell with colored chip
  const ActivityTypeCell = (cellProps: { dataItem: ActivityRecordRow }) => {
    return (
      <td>
        <Chip
          label={cellProps.dataItem.activityType}
          color={getActivityTypeColor(cellProps.dataItem.activityType)}
          size="small"
          variant="outlined"
        />
      </td>
    );
  };

  return (
    <div className={styles.creditDataContainer}>
      <div className={styles.creditDataHeader}>
        <DialogTitle id="form-dialog-title">{props.title}</DialogTitle>
        <ButtonGroup>
          <Button onClick={expandAll}>Expand All</Button>
          <Button onClick={collapseAll}>Collapse All</Button>
        </ButtonGroup>
      </div>

      <DialogContent className={styles.creditDataContent}>
        <Grid
          data={data.map(item => ({
            ...item,
            expanded: expandedItemIds.includes(item.id)
          }))}
          detail={(detailProps) => <DetailComponent dataItem={detailProps.dataItem} />}
          expandField="expanded"
          onExpandChange={onExpandChange}
          resizable={true}
        >
          <GridToolbar>
            <div className={styles.gridToolbar}>
              <span className={styles.gridToolbarText}>
                Total Records: {data.length}
              </span>
            </div>
          </GridToolbar>

          <GridColumn
            field="activityType"
            title="Activity"
            width="400px"
            cell={ActivityTypeCell}
          />
          <GridColumn
            field="httpStatusCode"
            title="HTTP Status"
            width="120px"
            cell={StatusCell}
          />
          <GridColumn
            field="summary"
            title="Summary"
            cell={(cellProps) => (
              <td>{cellProps.dataItem.activityType === 'Status Change'
                ? cellProps.dataItem.summary
                : getMessagePreview(cellProps.dataItem.summary)}</td>
            )}
          />
          <GridColumn
            field="modifiedBy"
            title="Who"
            width="200px"
            cell={(cellProps) => (
              <td>{getEmployeeName(props.employees, cellProps.dataItem.modifiedBy ?? cellProps.dataItem.createdBy)}</td>
            )}
          />
          <GridColumn
            field="createdDate"
            title="Date"
            width="190px"
            cell={(cellProps) => (
              <td>{formatDate(cellProps.dataItem.createdDate)}</td>
            )}
          />
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => props.closeDialog()}
          variant={"contained"}
          color="secondary"
        >
          Close
        </Button>
      </DialogActions>
    </div>
  );
};


interface DetailContentProps {
  record: ApplicationActivityRecord;
}

// Component for displaying formatted detail content (JSON/XML/plain text)
const DetailContent: React.FC<DetailContentProps> = (props) => {

  const formatMessage = (message: string): string => {
    if (!message) return '';
    
    const trimmed = message.trim();
    
    // Try to format as JSON
    try {
      const parsed = JSON.parse(trimmed);
      return JSON.stringify(parsed, null, 2);
    } catch {
      // Not valid JSON, check for XML
      if (trimmed.startsWith('<') && trimmed.includes('</')) {
        let formatted = '';
        let indent = 0;
        const parts = trimmed.replace(/>\s*</g, '><').split(/(<[^>]+>)/);
        
        for (const part of parts) {
          if (!part.trim()) continue;
          
          if (part.startsWith('</')) {
            indent = Math.max(0, indent - 1);
            formatted += '  '.repeat(indent) + part + '\n';
          } else if (part.startsWith('<') && part.endsWith('/>')) {
            formatted += '  '.repeat(indent) + part + '\n';
          } else if (part.startsWith('<')) {
            formatted += '  '.repeat(indent) + part + '\n';
            indent++;
          } else {
            formatted += '  '.repeat(indent) + part.trim() + '\n';
          }
        }
        return formatted.trim();
      }
    }
    
    return message;
  };

  return (
    <div className={styles.messageContent}>
      <div className={styles.messageContentHeader}>
        <div className={styles.formatIndicator}>
          Format: {props.record.contentType != null ? getEnumName(ApiMessageHistoryContentType, props.record.contentType) : 'Plain Text'}
        </div>
      </div>
      
      <div className={styles.tabContent}>
        <pre className={styles.codeContainer}>
          {formatMessage(props.record.detail ?? "")}
        </pre>
      </div>
    </div>
  );
};


interface QuoteChangeDiffProps {
  differencesJson: string;
}

// Component for displaying quote change differences as beautified JSON
const QuoteChangeDiffDetail: React.FC<QuoteChangeDiffProps> = (props) => {
  try {
    const parsed = JSON.parse(props.differencesJson);
    const formatted = JSON.stringify(parsed, null, 2);
    return (
      <pre className={styles.codeContainer}>
        {formatted}
      </pre>
    );
  } catch {
    return <pre className={styles.codeContainer}>{props.differencesJson}</pre>;
  }
};

// Component for displaying the detail view when a row is expanded
const DetailComponent: React.FC<{ dataItem: ActivityRecordRow }> = (props) => {
  const { dataItem } = props;
  const [copied, setCopied] = useState(false);

  if (!hasValue(dataItem.detail)) {
    return (
      <div className={styles.detailContainer}>
        <p style={{ color: '#999', fontStyle: 'italic' }}>No additional details available.</p>
      </div>
    );
  }

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(dataItem.detail ?? "").then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // For Desk/Post records with QuoteChangeDifferencesJson, render as beautified JSON
  const isDeskingWithDiffs = (dataItem.activityType === 'Post' || dataItem.activityType === 'Desk') && hasValue(dataItem.detail);

  return (
    <div className={styles.detailContainer}>
      <div className={styles.messageContentHeader}>
        <h3 className={styles.detailTitle} style={{ margin: 0 }}>
          {isDeskingWithDiffs ? 'Quote Change Differences' : 'Message Details'}
        </h3>
        <Button
          variant="outlined"
          size="small"
          onClick={handleCopyToClipboard}
        >
          {copied ? 'Copied!' : 'Copy to Clipboard'}
        </Button>
      </div>
      {isDeskingWithDiffs ? (
        <QuoteChangeDiffDetail differencesJson={dataItem.detail!} />
      ) : (
        <DetailContent record={dataItem} />
      )}
    </div>
  );
};